// ── Contractor Rob Flooring — InstallationInstructionsPage.js ──
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Wrench, Download } from "lucide-react";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    els.forEach((el) => el.setAttribute("data-reveal-init", ""));
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("in");
        }),
      { threshold: 0.08 },
    );
    els.forEach((el) => io.observe(el));
    const safety = setTimeout(
      () => els.forEach((el) => el.classList.add("in")),
      1500,
    );
    return () => {
      io.disconnect();
      clearTimeout(safety);
    };
  }, []);
}

function Section({ title, eyebrow, children }) {
  return (
    <div style={{ marginBottom: 48 }} data-reveal>
      {eyebrow && (
        <p
          style={{
            fontFamily: "var(--sans)",
            fontSize: 10,
            letterSpacing: ".22em",
            textTransform: "uppercase",
            color: "var(--saffron-deep)",
            fontWeight: 700,
            marginBottom: 6,
          }}>
          {eyebrow}
        </p>
      )}
      <h2
        style={{
          fontFamily: "var(--serif)",
          fontSize: 26,
          color: "var(--ink)",
          marginBottom: 16,
          paddingBottom: 10,
          borderBottom: "1px solid var(--rule)",
        }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul
      style={{
        fontFamily: "var(--sans)",
        fontSize: 14,
        color: "var(--ink-soft)",
        lineHeight: 1.8,
        paddingLeft: 22,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function SubfloorCard({ title, body }) {
  return (
    <div
      style={{
        padding: "20px 22px",
        background: "var(--ivory-soft)",
        borderRadius: "var(--r-sm)",
        border: "1px solid var(--rule)",
      }}>
      <p
        style={{
          fontFamily: "var(--sans)",
          fontSize: 13,
          fontWeight: 700,
          color: "var(--ink)",
          marginBottom: 8,
        }}>
        {title}
      </p>
      <p
        style={{
          fontFamily: "var(--sans)",
          fontSize: 13,
          color: "var(--ink-soft)",
          lineHeight: 1.65,
        }}>
        {body}
      </p>
    </div>
  );
}

export default function InstallationInstructionsPage() {
  useReveal();
  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top" />

      {/* Header */}
      <section
        style={{
          background: "var(--ivory-soft)",
          padding: "56px 0 40px",
          borderBottom: "1px solid var(--rule)",
        }}>
        <div className="page-container">
          <Link
            to="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "var(--sans)",
              fontSize: 12,
              color: "var(--ash)",
              letterSpacing: ".08em",
              textDecoration: "none",
              marginBottom: 24,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ash)")}>
            <ArrowLeft size={12} /> Back to Home
          </Link>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 12,
            }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(22,21,18,.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <Wrench size={18} style={{ color: "var(--ink)" }} />
            </div>
            <p className="eyebrow eyebrow-gold">Technical Documents</p>
          </div>
          <h1
            style={{
              fontFamily: "var(--serif)",
              fontSize: "clamp(36px,6vw,64px)",
              lineHeight: 1.0,
              textWrap: "balance",
            }}>
            Installation Instructions
          </h1>
          <p
            style={{
              fontFamily: "var(--sans)",
              fontSize: 14,
              color: "var(--ash)",
              marginTop: 12,
              maxWidth: 640,
              lineHeight: 1.65,
            }}>
            Uniclic® tongue and groove click-lock system — suitable for
            residential and light commercial interior applications. Please read
            all instructions before beginning installation.
          </p>
          {/* Download button */}
          <div style={{ marginTop: 20 }}>
            <a
              href="/lvp-installation-instructions.pdf"
              download="LVP-Installation-Instructions-ContractorRob.pdf"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
                letterSpacing: '.1em', textTransform: 'uppercase',
                color: 'var(--ink)', background: 'var(--saffron)',
                padding: '10px 20px', borderRadius: 'var(--r-sm)',
                textDecoration: 'none', transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--saffron-deep)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--saffron)'}
            >
              <Download size={13} /> Download as PDF
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: "64px 0 96px" }}>
        <div className="page-container" style={{ maxWidth: 820 }}>
          {/* Pro Tip */}
          <div
            style={{
              padding: "16px 20px",
              background: "rgba(232,179,57,.08)",
              border: "1px solid var(--saffron)",
              borderRadius: "var(--r-sm)",
              marginBottom: 48,
              display: "flex",
              gap: 12,
            }}
            data-reveal>
            <span
              style={{
                fontFamily: "var(--serif)",
                fontSize: 20,
                color: "var(--saffron-deep)",
                flexShrink: 0,
                lineHeight: 1,
              }}>
              ★
            </span>
            <div>
              <p
                style={{
                  fontFamily: "var(--sans)",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--ink)",
                  marginBottom: 4,
                }}>
                Pro Tip
              </p>
              <p
                style={{
                  fontFamily: "var(--sans)",
                  fontSize: 13,
                  color: "var(--ink-soft)",
                  lineHeight: 1.6,
                }}>
                Purchase an additional 5% of flooring to allow for cuts, and an
                additional 10%–15% if installing diagonally. Excess flooring
                should be stored flat in a climate-controlled area for future
                repairs.
              </p>
            </div>
          </div>

          <Section
            title="Requirements & Site Conditions"
            eyebrow="Before You Begin">
            <BulletList
              items={[
                'Make sure the sub-floor is flat, clean, and dry. Any uneven areas greater than 3/16" in 10ft must be leveled with leveling compound.',
                "Maximum installation area: 10m (32.8ft) in length and width. T-molding is required for larger areas.",
                "Interior use only in a climate-controlled environment. Not to be exposed to direct sunlight.",
                "Keep records of levelness and moisture testing.",
                "DO NOT use separate pad for pre-attached vinyl planks.",
                "DO NOT install over floors that are sloped for drainage.",
                "Residential and light commercial applications only. NOT for use on ramps.",
                "Install length of boards parallel to the main light direction for best visual results.",
              ]}
            />
          </Section>

          <Section title="Prior to Installation" eyebrow="Preparation">
            <BulletList
              items={[
                "Acclimate vinyl planks at least 48 hours to room temperature (60°–85°F) and relative humidity between 30%–55%.",
                "Use a humidifier to maintain relative humidity if needed. Install when conditions are representative of typical year-round conditions.",
                "DO NOT stack cartons higher than 10 units. DO NOT store cartons directly on concrete — allow for air circulation.",
                "Carefully check all flooring materials for defects. Contact your supplier immediately if any defect is found.",
                "Open several cartons and mix planks together to ensure a natural pattern throughout installation.",
                "Install all cabinets prior to installing flooring (DO NOT install flooring under fixed cabinets).",
                "Remove all furniture and existing quarter round, baseboard molding, or cove base.",
                "Inspect subfloor to be dry, clean, solid, and flat. Remove all adhesive residue.",
                'Undercut doorway moldings to the thickness of the flooring. Verify doors can open after installation (min. +1cm / 3/8").',
                "DO NOT install over soft subfloors (carpet) or areas prone to flooding.",
              ]}
            />
          </Section>

          <Section title="Required Tools" eyebrow="Tools">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[
                "Carpenter square",
                "Utility knife",
                "Tape measure",
                "Pencil",
                "Long straight edge",
                "Chalk line",
                "Safety glasses",
                "Tapping block",
                "Pull bar",
              ].map((tool) => (
                <span key={tool} className="chip">
                  {tool}
                </span>
              ))}
            </div>
          </Section>

          <Section title="Subfloor Requirements" eyebrow="Subfloor">
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <SubfloorCard
                title="Concrete Subfloors"
                body="Moisture vapor emissions must not exceed 5 lbs./24hr per 1,000 sq.ft (ASTM F1869) or 85% RH (ASTM F2170). Level all uneven areas greater than 3/16 in 10ft. Patch all holes and cracks with latex patching compound. Newly poured concrete must cure a minimum of 90 days. Apply a 6-mil polyethylene moisture barrier before installation. It is the installer's responsibility to ensure a smooth, dry substrate."
              />
              <SubfloorCard
                title="Wood Subfloors"
                body="Must be solid and flat. Sand down high spots and fill low spots with leveling compound. Firmly fasten all loose boards and fill all gaps before installation. DO NOT install vinyl planks over wood floors or laminate laid directly on concrete or bitumen."
              />
              <SubfloorCard
                title="Mosaics / Terrazzo / Ceramics"
                body="Must be solid and flat. Level grout lines with a width or depth greater than 3/16 (5mm) with leveling compound before installation."
              />
              <SubfloorCard
                title="Textile Floor Covering (Carpet)"
                body="Must be fully removed before installation. Do not install LVP over carpet."
              />
              <SubfloorCard
                title="Radiant Heat Subfloors"
                body="LVP can be installed over in-floor radiant heating systems provided the subfloor surface does not exceed 85°F (29.44°C) at any point. Initial floor temperature should not exceed 70°F (21.11°C) for 48 hours prior, during, and after installation. Gradually increase to the desired setting up to 85°F after installation. Additional expansion gaps may be required. Radiant heating systems installed directly on top of the subfloor surface are not recommended."
              />
            </div>
          </Section>

          <div
            style={{
              marginTop: 64,
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
            }}
            data-reveal>
            <Link
              to="/technical/data-sheet"
              className="btn btn-outline"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              ← Technical Data Sheet
            </Link>
            <Link
              to="/technical/warranty"
              className="btn btn-outline"
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              ← Warranty
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
